/* Marshmallo Bold — behaviour for mm-* sections */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Line-by-line reveal when the element enters the viewport.
  if (!customElements.get('mm-reveal')) {
    customElements.define(
      'mm-reveal',
      class extends HTMLElement {
        connectedCallback() {
          this.classList.add('mm-reveal');
          [...this.children].forEach((line, i) => line.style.setProperty('--i', i));
          if (reduceMotion) return this.classList.add('is-in');
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((e) => e.isIntersecting)) {
                this.classList.add('is-in');
                this.observer.disconnect();
              }
            },
            { threshold: 0.2 }
          );
          this.observer.observe(this);
        }
        disconnectedCallback() {
          this.observer?.disconnect();
        }
      }
    );
  }

  // Scroll progress (-1..1 around the viewport centre) exposed as --mm-p for parallax.
  if (!customElements.get('mm-parallax')) {
    customElements.define(
      'mm-parallax',
      class extends HTMLElement {
        connectedCallback() {
          if (reduceMotion) return;
          this.onScroll = () => {
            if (this.ticking) return;
            this.ticking = true;
            requestAnimationFrame(() => {
              const r = this.getBoundingClientRect();
              const vh = window.innerHeight;
              const p = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
              this.style.setProperty('--mm-p', Math.max(-1, Math.min(1, p)).toFixed(3));
              this.ticking = false;
            });
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        }
        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
        }
      }
    );
  }

  // Hero: plays the visible video of the active slide, sound toggle, pause off screen.
  // With data-rotate, moves to the next slide when its video ends (image slides: data-interval seconds).
  if (!customElements.get('mm-hero')) {
    customElements.define(
      'mm-hero',
      class extends HTMLElement {
        connectedCallback() {
          // Full-screen means "below the header and announcement bar", so claim and buttons stay above the fold.
          const top = this.getBoundingClientRect().top + window.scrollY;
          this.style.setProperty('--mm-header-h', `${Math.max(0, Math.round(top))}px`);
          this.slides = [...this.querySelectorAll('.mm-hero__slide')];
          this.dots = [...this.querySelectorAll('[data-mm-slide]')];
          this.videos = [...this.querySelectorAll('video')];
          this.rotate = this.hasAttribute('data-rotate') && this.slides.length > 1;
          this.current = 0;
          this.videos.forEach((v) => {
            v.muted = true;
            if (!this.rotate) return;
            v.addEventListener('ended', () => this.isShown(v) && this.next());
            v.addEventListener('timeupdate', () => {
              if (this.isShown(v) && v.duration) this.setProgress(v.currentTime / v.duration);
            });
          });
          this.querySelector('[data-mm-sound]')?.addEventListener('click', (e) => {
            const unmute = this.videos[0].muted;
            this.videos.forEach((v) => (v.muted = !unmute));
            e.currentTarget.setAttribute('aria-pressed', String(unmute));
          });
          this.dots.forEach((d) => d.addEventListener('click', () => this.show(Number(d.dataset.mmSlide), true)));
          this.sync = () => {
            this.videos.forEach((v) => {
              if (this.inView && this.isShown(v) && !reduceMotion) v.play().catch(() => {});
              else v.pause();
            });
            this.schedule();
          };
          this.observer = new IntersectionObserver(([e]) => {
            this.inView = e.isIntersecting;
            this.sync();
          });
          this.observer.observe(this);
          this.mql = window.matchMedia('(min-width: 750px)');
          this.mql.addEventListener('change', this.sync);
          this.onEditorSelect = (e) => {
            const i = this.slides.indexOf(e.target);
            if (i > -1) this.show(i, true);
          };
          document.addEventListener('shopify:block:select', this.onEditorSelect);
        }
        disconnectedCallback() {
          this.observer?.disconnect();
          this.mql?.removeEventListener('change', this.sync);
          document.removeEventListener('shopify:block:select', this.onEditorSelect);
          clearTimeout(this.timer);
        }
        // A video counts when it sits in the active slide and is not hidden at this breakpoint.
        isShown(v) {
          return v.closest('.mm-hero__slide') === this.slides[this.current] && getComputedStyle(v).display !== 'none';
        }
        activeVideo() {
          return [...this.slides[this.current].querySelectorAll('video')].find((v) => this.isShown(v));
        }
        // Image slides advance on a timer; video slides on 'ended', with a backstop in case the video stalls.
        schedule() {
          clearTimeout(this.timer);
          if (!this.rotate || !this.inView || reduceMotion) return;
          const v = this.activeVideo();
          if (v) {
            const left = (v.duration || 20) - v.currentTime + 4;
            this.timer = setTimeout(() => this.next(), left * 1000);
            return;
          }
          const s = Number(this.dataset.interval) || 8;
          this.setProgress(1, s);
          this.timer = setTimeout(() => this.next(), s * 1000);
        }
        // Fills the active dot: p is 0..1; with seconds, animates from empty to full over that time.
        setProgress(p, seconds) {
          const bar = this.dots[this.current]?.querySelector('span');
          if (!bar) return;
          bar.style.setProperty('--mm-prog-dur', seconds ? `${seconds}s` : '0.25s');
          if (seconds) {
            bar.style.setProperty('--mm-prog', 0);
            void bar.offsetWidth;
          }
          bar.style.setProperty('--mm-prog', p);
        }
        next() {
          this.show((this.current + 1) % this.slides.length);
        }
        show(i, fromUser) {
          if (i === this.current && !fromUser) return;
          const prev = this.slides[this.current];
          prev.classList.remove('is-active');
          prev.setAttribute('aria-hidden', 'true');
          prev.inert = true;
          this.dots.forEach((d, j) => {
            d.classList.toggle('is-active', j === i);
            d.classList.toggle('is-done', j < i);
            if (j === i) d.setAttribute('aria-current', 'true');
            else d.removeAttribute('aria-current');
            const bar = d.querySelector('span');
            bar.style.setProperty('--mm-prog-dur', '0s');
            bar.style.setProperty('--mm-prog', 0);
          });
          this.current = i;
          const slide = this.slides[i];
          slide.classList.add('is-active');
          slide.removeAttribute('aria-hidden');
          slide.inert = false;
          slide.querySelectorAll('video').forEach((v) => (v.currentTime = 0));
          // Replay the headline reveal.
          const title = slide.querySelector('mm-reveal');
          if (title && !reduceMotion) {
            title.classList.remove('is-in');
            void title.offsetWidth;
            title.classList.add('is-in');
          }
          this.sync();
        }
      }
    );
  }

  // Countdown to data-end (ISO date with timezone); shows data-done afterwards.
  if (!customElements.get('mm-countdown')) {
    customElements.define(
      'mm-countdown',
      class extends HTMLElement {
        connectedCallback() {
          this.end = Date.parse(this.dataset.end);
          if (Number.isNaN(this.end)) return;
          this.time = this.querySelector('.mm-countdown__time');
          this.hidden = false;
          const pad = (n) => String(n).padStart(2, '0');
          const tick = () => {
            const left = Math.max(0, this.end - Date.now());
            if (!left) {
              this.querySelector('.mm-countdown__label').textContent = this.dataset.done;
              this.time.textContent = '';
              return clearInterval(this.timer);
            }
            const s = Math.floor(left / 1000);
            const d = Math.floor(s / 86400);
            this.time.textContent = `${d}d ${pad(Math.floor((s % 86400) / 3600))}h ${pad(Math.floor((s % 3600) / 60))}m ${pad(s % 60)}s`;
          };
          tick();
          this.timer = setInterval(tick, 1000);
        }
        disconnectedCallback() {
          clearInterval(this.timer);
        }
      }
    );
  }

  // One reel: plays its video, or cycles its photos with Ken Burns, while it is visible.
  if (!customElements.get('mm-reel')) {
    customElements.define(
      'mm-reel',
      class extends HTMLElement {
        connectedCallback() {
          this.video = this.querySelector('video');
          this.slides = [...this.querySelectorAll('.mm-reel__slide')];
          this.bars = [...this.querySelectorAll('.mm-reel__bar')];
          this.slideMs = Number(this.dataset.slideMs) || 3200;
          this.index = 0;
          this.style.setProperty('--mm-slide', `${this.slideMs}ms`);

          if (this.video) {
            this.video.muted = true;
            this.video.addEventListener('ended', () => this.video.play());
          }
          this.querySelector('.mm-reel__mute')?.addEventListener('click', (e) => {
            const unmute = this.video.muted;
            // Only one reel speaks at a time.
            if (unmute) document.querySelectorAll('mm-reel').forEach((r) => r !== this && r.mute?.());
            this.video.muted = !unmute;
            e.currentTarget.setAttribute('aria-pressed', String(unmute));
          });

          const look = this.querySelector('dialog');
          this.querySelector('[data-mm-look-open]')?.addEventListener('click', () => {
            this.pause();
            look.showModal();
          });
          look?.addEventListener('close', () => this.visible && this.play());
          look?.addEventListener('click', (e) => e.target === look && look.close());

          this.addEventListener('mouseenter', () => this.play());
        }
        mute() {
          if (!this.video) return;
          this.video.muted = true;
          this.querySelector('.mm-reel__mute')?.setAttribute('aria-pressed', 'false');
        }
        play() {
          if (this.playing || reduceMotion) return;
          this.playing = true;
          if (this.video) this.video.play().catch(() => {});
          else if (this.slides.length > 1) this.showSlide(this.index);
          this.tick();
        }
        pause() {
          this.playing = false;
          cancelAnimationFrame(this.raf);
          this.video?.pause();
        }
        showSlide(i) {
          this.index = i % this.slides.length;
          this.slideStart = performance.now();
          this.slides.forEach((s, n) => {
            s.classList.toggle('is-active', n === this.index);
            // Restart the Ken Burns animation on the incoming slide.
            if (n === this.index) {
              const img = s.querySelector('img');
              if (img) {
                img.style.animation = 'none';
                void img.offsetWidth;
                img.style.animation = '';
              }
            }
          });
          this.bars.forEach((b, n) => {
            b.classList.toggle('is-done', n < this.index);
            b.firstElementChild.style.transform = n < this.index ? 'scaleX(1)' : 'scaleX(0)';
          });
        }
        tick() {
          if (!this.playing) return;
          let progress = 0;
          if (this.video && this.video.duration) {
            progress = this.video.currentTime / this.video.duration;
          } else if (this.slides.length > 1) {
            progress = (performance.now() - this.slideStart) / this.slideMs;
            if (progress >= 1) {
              this.showSlide(this.index + 1);
              progress = 0;
            }
          }
          const bar = this.bars[this.video ? 0 : this.index];
          if (bar) bar.firstElementChild.style.transform = `scaleX(${Math.min(progress, 1)})`;
          this.raf = requestAnimationFrame(() => this.tick());
        }
      }
    );
  }

  // Rail: arrows and visibility-driven autoplay.
  if (!customElements.get('mm-reels')) {
    customElements.define(
      'mm-reels',
      class extends HTMLElement {
        connectedCallback() {
          this.rail = this.querySelector('.mm-reels__rail');
          this.querySelectorAll('[data-mm-dir]').forEach((btn) =>
            btn.addEventListener('click', () => {
              const card = this.rail.querySelector('mm-reel');
              const step = card ? card.offsetWidth + 16 : this.rail.clientWidth * 0.8;
              this.rail.scrollBy({ left: step * Number(btn.dataset.mmDir), behavior: 'smooth' });
            })
          );
          if (this.dataset.autoplay !== 'true') return;
          this.observer = new IntersectionObserver(
            (entries) =>
              entries.forEach((e) => {
                e.target.visible = e.intersectionRatio >= 0.6;
                e.target.visible ? e.target.play() : e.target.pause();
              }),
            { threshold: [0, 0.6, 1] }
          );
          this.querySelectorAll('mm-reel').forEach((r) => this.observer.observe(r));
        }
        disconnectedCallback() {
          this.observer?.disconnect();
          this.querySelectorAll('mm-reel').forEach((r) => r.pause());
        }
      }
    );
  }

  // Shop the look: add to cart without leaving the page, then refresh the header bubble.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-mm-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const status = form.parentElement.querySelector('.mm-look__status');
    const label = button.innerHTML;
    button.disabled = true;
    button.textContent = form.dataset.addingLabel;
    try {
      const body = new FormData(form);
      body.append('sections', 'cart-icon-bubble');
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message);
      const bubble = document.getElementById('cart-icon-bubble');
      const html = data.sections?.['cart-icon-bubble'];
      if (bubble && html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const inner = parsed.querySelector('.shopify-section');
        if (inner) bubble.innerHTML = inner.innerHTML;
      }
      if (window.publish && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'mm-look', productVariantId: body.get('id') });
      }
      status.innerHTML = form.dataset.addedMessage;
      button.innerHTML = '✓';
      setTimeout(() => (button.innerHTML = label), 1600);
    } catch (err) {
      status.textContent = err.message || form.dataset.errorMessage;
      button.innerHTML = label;
    } finally {
      button.disabled = false;
    }
  });
})();
